import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
    Button,
    Box,
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    HStack,
    Heading,
    Grid,
    Icon,
} from '@chakra-ui/react';
import Alert from 'components/v4/Alert';
import AlertIcon from 'components/v4/AlertIcon';

import SkeletonPlaceholder from 'components/v4/Cards/ServiceProfileCardPlaceholder';
import ServicesAddCard from './ServicesAddCard';

import path from '@/routes/constants';
import { useNavigateLocation } from 'hooks/useNavigateLocation/useNavigateLocation';
import CardDetails from '../components/CardDetails';
import { PlatformEnums } from 'utils/platforms';
import getCompanyPlatforms from 'API/Company/getCompanyPlatforms';

const AvailableServiceProfiles = ({ serviceProfiles, serviceWizard }) => {
    const { handleNavigateLocation } = useNavigateLocation();
    const { requiredScope } = useSelector((state) => state.login);
    const { currentCompany, currentCompanyPlatforms } = useSelector(
        (state) => state.navigationLists,
    );
    const dispatch = useDispatch();

    useQuery({
        queryKey: [
            'services',
            `companyPlatforms: ${currentCompany}, requiredScope: ${requiredScope}`,
        ],
        queryFn: async () => {
            const res = await getCompanyPlatforms(
                requiredScope,
                currentCompany,
            );
            dispatch({
                type: 'CHANGE_CURRENT_COMPANY_PLATFORMS',
                payload: res,
            });
            return res;
        },
        enabled: Boolean(currentCompany && !currentCompanyPlatforms?.length),
        refetchOnWindowFocus: false,
    });

    const hasOperatorPlatform = useMemo(() => {
        return currentCompanyPlatforms?.some(
            (platform) => platform?.id === PlatformEnums.OPERATOR_PLATFORM,
        );
    }, [currentCompanyPlatforms]);
    const { darkMode } = useSelector((state) => state.settings);

    const operatorPlatformName = useMemo(() => {
        const operatorPlatform = serviceProfiles?.find(
            (sp) => sp.serviceType === 'OperatorPlatform',
        );
        if (operatorPlatform) {
            return operatorPlatform?.customDisplayName || 'Operator Platform';
        }
        return 'Operator Platform';
    }, [serviceProfiles]);

    const groupedServiceProfiles = useMemo(() => {
        if (!serviceProfiles?.length) return {};

        const filteredProfiles = hasOperatorPlatform
            ? serviceProfiles.filter(
                  (sp) => sp.serviceType !== 'OperatorPlatform',
              )
            : serviceProfiles;

        const grouped = filteredProfiles.reduce((acc, serviceProfile) => {
            const cardDetails = CardDetails(
                serviceProfile.serviceType,
                serviceProfile?.serviceDefinition?.platformID,
            );
            const addType = cardDetails?.addType || 'Other';

            if (!acc[addType]) {
                acc[addType] = {
                    services: [],
                    icon: null, // You can add icons based on addType if needed
                };
            }
            acc[addType].services.push(serviceProfile);
            return acc;
        }, {});

        // Sort services within each category (optional)
        for (const category in grouped) {
            grouped[category].services.sort((a, b) => {
                const aDetails = CardDetails(a.serviceType);
                const bDetails = CardDetails(b.serviceType);
                return (aDetails?.serviceName || '').localeCompare(
                    bDetails?.serviceName || '',
                );
            });
        }

        // Define category order (you can customize this)
        const categoryOrder = ['Platform', 'Other'];
        const sortedGroupedEntries = categoryOrder
            .filter(
                (categoryTitle) =>
                    grouped[categoryTitle] &&
                    grouped[categoryTitle].services.length > 0,
            )
            .map((categoryTitle) => [categoryTitle, grouped[categoryTitle]]);

        // Add any remaining categories not in the predefined order
        Object.keys(grouped).forEach((categoryTitle) => {
            if (
                !categoryOrder.includes(categoryTitle) &&
                grouped[categoryTitle].services.length > 0
            ) {
                sortedGroupedEntries.push([
                    categoryTitle,
                    grouped[categoryTitle],
                ]);
            }
        });

        return Object.fromEntries(sortedGroupedEntries);
    }, [serviceProfiles, hasOperatorPlatform]);

    if (serviceProfiles === null || serviceProfiles === undefined) {
        return (
            <SkeletonPlaceholder
                style={{ maxWidth: '1064px' }}
                skeletons={13}
                rows={1}
                columns={4}
                gap={4}
                items={4}
            />
        );
    }

    if (!serviceProfiles?.length) {
        return (
            <Alert
                status="info"
                sx={{
                    w: '1000px',
                    marginBottom: '30px',
                    whiteSpace: 'pre-line',
                }}>
                <AlertIcon />
                Your partner does not have any additional services which can be
                added. &nbsp;
                {requiredScope > 40 && (
                    <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={() => {
                            handleNavigateLocation(`/${path.SERVICE_PROFILES}`);
                            dispatch({
                                type: 'CHANGE_MINIMUM_SCOPE',
                                payload: 40,
                            });
                        }}>
                        Go to service profiles
                    </Button>
                )}
            </Alert>
        );
    }

    return (
        <>
            <Box mb={6} style={{ maxWidth: '1064px' }}>
                {Object.entries(groupedServiceProfiles).map(
                    ([categoryTitle, categoryData]) => (
                        <Accordion
                            key={categoryTitle}
                            defaultIndex={[0]}
                            allowToggle
                            width="1000px"
                            mb={4}>
                            <AccordionItem
                                key={categoryTitle}
                                borderRadius="8px"
                                borderTop="none"
                                borderBottom="none"
                                bg="transparent">
                                {() => (
                                    <>
                                        <HStack spacing={0} position="relative">
                                            <AccordionButton
                                                flex="1"
                                                borderRadius="8px"
                                                paddingTop={3}
                                                paddingBottom={3}
                                                bg={
                                                    darkMode ? 'black' : 'white'
                                                }
                                                _hover={{
                                                    bg: darkMode
                                                        ? 'blue.900'
                                                        : 'blue.50',
                                                }}>
                                                <HStack
                                                    spacing={3}
                                                    flex="1"
                                                    textAlign="left">
                                                    {categoryData.icon && (
                                                        <Icon
                                                            as={
                                                                categoryData.icon
                                                            }
                                                            boxSize={6}
                                                            color={
                                                                darkMode
                                                                    ? 'white'
                                                                    : 'black'
                                                            }
                                                        />
                                                    )}
                                                    <Heading
                                                        size="md"
                                                        color={
                                                            darkMode
                                                                ? 'white'
                                                                : 'black'
                                                        }>
                                                        {categoryTitle ===
                                                        'Other'
                                                            ? 'Other Services'
                                                            : categoryTitle}
                                                        <Box
                                                            as="span"
                                                            ml={2}
                                                            fontSize="sm"
                                                            opacity={0.7}>
                                                            (
                                                            {
                                                                categoryData
                                                                    .services
                                                                    .length
                                                            }
                                                            )
                                                        </Box>
                                                    </Heading>
                                                </HStack>
                                                <Box
                                                    as="span"
                                                    flex="0"
                                                    textAlign="right">
                                                    <AccordionIcon />
                                                </Box>
                                            </AccordionButton>
                                        </HStack>
                                        <AccordionPanel
                                            pb={4}
                                            px={0}
                                            bg="transparent">
                                            <Grid
                                                templateColumns="repeat(4, 1fr)"
                                                gap={4}
                                                py={2}>
                                                {categoryData.services.map(
                                                    (serviceProfile) => {
                                                        const cardDetails =
                                                            CardDetails(
                                                                serviceProfile.serviceType,
                                                                serviceProfile
                                                                    ?.serviceDefinition
                                                                    ?.platformID,
                                                            );
                                                        const isOperatorPlatformCategory =
                                                            cardDetails?.addType ===
                                                            'Operator Platform Services';
                                                        const isTheOperatorPlatform =
                                                            serviceProfile.serviceType ===
                                                            'OperatorPlatform';

                                                        return (
                                                            <ServicesAddCard
                                                                key={
                                                                    serviceProfile.id
                                                                }
                                                                serviceWizard={
                                                                    serviceWizard
                                                                }
                                                                serviceProfile={
                                                                    serviceProfile
                                                                }
                                                                operatorPlatformName={
                                                                    operatorPlatformName
                                                                }
                                                                isButtonDisabled={
                                                                    !hasOperatorPlatform &&
                                                                    isOperatorPlatformCategory &&
                                                                    !isTheOperatorPlatform
                                                                }
                                                            />
                                                        );
                                                    },
                                                )}
                                            </Grid>
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        </Accordion>
                    ),
                )}
            </Box>
        </>
    );
};

export default AvailableServiceProfiles;
